<template>
  <div class="datepicker-container">
    <!-- Calendar Header -->
    <div class="calendar-header">
      <button @click="prevMonth" class="nav-btn">&lt;</button>
      <h2>{{ monthYear }}</h2>
      <button @click="nextMonth" class="nav-btn">&gt;</button>
    </div>

    <!-- Weekday Labels -->
    <div class="calendar-grid">
      <div v-for="day in weekDays" :key="day" class="weekday">{{ day }}</div>

      <!-- Calendar Days -->
      <div
        v-for="(date, index) in calendarDays"
        :key="index"
        class="calendar-day"
        :class="{
          'disabled': !isDateAvailable(date),
          'selected': isSelected(date),
          'special-day': isSpecialDay(date)
        }"
        :style="getDayStyle(date)"
        @click="selectDate(date)"
      >
        {{ date ? date.getDate() : '' }}
        <div v-if="isSpecialDay(date)" class="day-indicator"></div>
      </div>
    </div>

    <!-- Special Days Legend -->
    <div class="legend">
      <div class="legend-item">
        <span class="legend-color" style="background-color: #f5739f;"></span>
        <span>Ladies Day</span>
      </div>
      <div class="legend-item">
        <span class="legend-color" style="background-color: #b4c016;"></span>
        <span>Family Day</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';

const props = defineProps({
  modelValue: {
    type: Date,
    default: null
  },
  availableDates: {
    type : Array,
    default : () => []
  }
});

console.log(props.availableDates);


const emit = defineEmits(['update:modelValue']);

const currentDate = ref(new Date());
const selectedDate = ref(null);

// Week days array
const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Formatted month and year
const monthYear = computed(() => {
  const options = { month: 'long', year: 'numeric' };
  return currentDate.value.toLocaleDateString('en-US', options);
});

// Generate calendar days
const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Add empty slots for days before the first day of month
  const daysArray = Array(firstDay.getDay()).fill(null);
  
  // Add all days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    daysArray.push(new Date(year, month, day));
  }
  
  return daysArray;
});

// Navigation methods
const prevMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1
  );
};

const nextMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1
  );
};

// Date helper methods
const formatDate = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

const isDateAvailable = (date) => {
  if (!date) return false;
  return props.availableDates.some(d => d.date === formatDate(date));
};

const isSpecialDay = (date) => {
  if (!date) return false;
  const dateInfo = props.availableDates.find(d => d.date === formatDate(date));
  return dateInfo?.is_special;
};

const isSelected = (date) => {
  if (!date || !selectedDate.value) return false;
  return formatDate(date) === formatDate(selectedDate.value);
};

const getDayStyle = (date) => {
  if (!date) return {};
  const dateInfo = props.availableDates.find(d => d.date === formatDate(date));
  if (dateInfo?.is_special) {
    return {
      backgroundColor: dateInfo.special_color,
      color: '#ffffff'
    };
  }
  return {};
};

const selectDate = (date) => {
  if (!date || !isDateAvailable(date)) return;
  
  selectedDate.value = date;
  emit('update:modelValue', date);
  
  const dateInfo = props.availableDates.find(d => d.date === formatDate(date));
  if (dateInfo?.is_special) {
    console.log('Special Day:', {
      title: dateInfo.title,
      description: dateInfo.description
    });
  }
};

</script>

<style scoped>
.datepicker-container {
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  margin: 0 auto;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.nav-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 50%;
}

.nav-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
}

.weekday {
  text-align: center;
  font-weight: 600;
  color: #666;
  padding: 0.5rem;
}

.calendar-day {
  position: relative;
  text-align: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
}

.calendar-day:hover:not(.disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.calendar-day.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.calendar-day.selected {
  background-color: #e5e7eb;
}

.day-indicator {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: currentColor;
}

.legend {
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
}

/* RTL Support */
[dir="rtl"] .calendar-header {
  flex-direction: row-reverse;
}

[dir="rtl"] .legend-item {
  flex-direction: row-reverse;
}
</style>